import { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGridPro, GridInputRowSelectionModel, GridColDef } from '@mui/x-data-grid-pro';
import { SubMenu } from './SubMenu';
import { useTranslation } from 'react-i18next';
import { TimekeeperService } from 'services/TimekeeperService';
import { DatePatternBase } from 'repository/timekeeper/@types/DatePattern';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
//import { PatternStartDateEnum } from 'repository/timekeeper/@types/TypePatternStartDate';

type ResponseDatePattern = DatePatternBase;

const DatePatternTable = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedDatePattern, setSelectedDatePattern] = useState<GridInputRowSelectionModel | undefined>([]);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selected, setSelected] = useState<any>([]);

    const timekeeperService = useMemo(() => new TimekeeperService(), []);

    const getFrequencyLabel = useCallback((frequencyType: FrequencyUnitTime) => {
        const frequency_map: Record<FrequencyUnitTime, string> = {
            "DIAS": t("accruals.date_patterns.dialog.inputs.options_frequency.days"),
            "SEMANAS": t("accruals.date_patterns.dialog.inputs.options_frequency.weeks"),
            "MESES": t("accruals.date_patterns.dialog.inputs.options_frequency.months"),
            "ANOS": t("accruals.date_patterns.dialog.inputs.options_frequency.years"),
            "PERIODO_PAGO": t("accruals.date_patterns.dialog.inputs.options_frequency.periods_of_pay")
        };
        return frequency_map[frequencyType] || frequencyType;
    }, [t]);

    /* const start_pattern = useMemo(() => ({
        fecha_establecimiento: t("accruals.date_patterns.dialog.inputs.types_date_selectable.establishment_date"),
        fecha_especifica_persona: t("accruals.date_patterns.dialog.inputs.types_date_selectable.specific_date_person")
    }), [t]); */

    const columns: GridColDef[] = [
        { field: 'name', headerName: t("accruals.date_patterns.table.columns.name"), flex: 1 },
        { field: 'date_reference', headerName: t("accruals.date_patterns.table.columns.date_reference"), flex: 1, headerAlign: 'center', align: 'center', filterable: false },
        { field: 'frequency', headerName: t("accruals.date_patterns.table.columns.frequency"), flex: 1, headerAlign: 'center', align: 'center', filterable: false},
    ];

    const reloading = useCallback(() => {
        setLoading(true);
        timekeeperService.date_pattern.getAll({ include: 'reference_date', })
            .then((response: ResponseDatePattern[]) => {
                const date_pattern = response.map((data) => ({
                    id: data.date_pattern_id,
                    name: data.name,
                    date_reference: data.reference_date ? (data.reference_date.name ? data.reference_date.name : '-') : '-',
                    frequency: getFrequencyLabel(data.frequency_unit_time as FrequencyUnitTime),

                }));
                setRows(date_pattern);
            })
            .catch((error) => {
                console.error('Error al obtener los patrones de fecha:', error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [timekeeperService.date_pattern, getFrequencyLabel]);

    useEffect(() => {
        reloading();
    }, [reloading]);

    const cleanSelected = () => {
        setSelected([]);
        setSelectedDatePattern([]);
    }

    return (
        <div style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null)
            }}
        >
            <DataGridPro
                sx={{ height: 'calc(100vh - 140px)' }}
                columns={columns}
                rows={rows}
                loading={loading}
                rowSelectionModel={selectedDatePattern}
                pagination
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let row = rows.filter((row) => newRowSelectionModel.includes(row.id))
                    setSelectedDatePattern(newRowSelectionModel);
                    setSelected(row);

                }}
                checkboxSelection
                localeText={
                    {
                        headerFilterOperatorContains: t('general.table.headerFilters.headerFilterOperatorContains'),
                        headerFilterOperatorEquals: t('general.table.headerFilters.headerFilterOperatorEquals'),
                        headerFilterOperatorStartsWith: t('general.table.headerFilters.headerFilterOperatorStartsWith'),
                        headerFilterOperatorEndsWith: t('general.table.headerFilters.headerFilterOperatorEndsWith'),
                        headerFilterOperatorIsEmpty: t('general.table.headerFilters.headerFilterOperatorIsEmpty'),
                        headerFilterOperatorIsNotEmpty: t('general.table.headerFilters.headerFilterOperatorIsNotEmpty'),
                        footerRowSelected: (count) =>
                            count > 1 ? `${count.toLocaleString()} filas seleccionadas`
                                : `${count.toLocaleString()} fila seleccionada`,

                        footerTotalRows: t('general.table.footerFilters.footerTotalRows'),
                        MuiTablePagination: {
                            labelDisplayedRows: ({ from, to, count }) =>
                                `${from} - ${to} of ${count === -1 ? `more than ${to}` : count}`,
                            labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                        },
                    }
                }
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnMenu
                headerFilters
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    }
                }}
            />
            <SubMenu
                selected={selected}
                cleanSelected={cleanSelected}
                rows={rows}
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                reloading={reloading}
            />
        </div>
    );
}

export { DatePatternTable };